import { RotatingLine } from "../motion/RotatingLine";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowRight, Sparkles } from "lucide-react";

// Word rises from a mask, one by one.
const HeroWord = ({ i, italic, className = "", children }) => (
  <span className={`word-mask ${className}`}>
    <span className={`word-rise ${italic ? "italic" : ""}`} style={{ animationDelay: `${i * 80}ms` }}>
      {children}
    </span>
  </span>
);

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1714636608872-048fc9231892?auto=format&fit=crop&w=1600&q=60&fm=webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.45,
        }}
      />
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(247,245,240,0.3) 0%, rgba(247,245,240,0.85) 55%, #F7F5F0 100%)",
        }}
      />
      <div className="noise-overlay -z-10" />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
        {/* Overline */}
        <div className="col-span-12 flex items-center gap-3 anim-fade-up">
          <div className="eyebrow text-ink" data-testid="hero-overline">
            {t("hero.overline")}
          </div>
          <div className="h-px flex-1 bg-green max-w-[200px] draw-line" />
        </div>

        {/* Title */}
        <h1
          data-testid="hero-title"
          className="col-span-12 md:col-span-11 heading-display text-[clamp(3rem,9vw,9.5rem)] mt-6"
        >
          <HeroWord i={0}>{t("hero.title_a")}</HeroWord>
          <HeroWord i={1} italic className="ml-[0.3em] text-ink">{t("hero.title_b")}</HeroWord>
          <br />
          <HeroWord i={2}>{t("hero.title_c")}</HeroWord>
          <HeroWord i={3} italic className="ml-[0.3em] font-normal">{t("hero.title_d")}</HeroWord>
        </h1>

        {/* Promise + supporting line + rotating line */}
        <div className="col-span-12 md:col-span-8 lg:col-span-7 mt-10 anim-fade-up delay-300">
          <p data-testid="hero-promise" className="heading-display text-2xl md:text-3xl lg:text-[2.1rem] leading-snug max-w-[760px]">
            {t("hero.promise")}
          </p>
          <p data-testid="hero-sub" className="mt-6 text-lg md:text-xl text-ink max-w-[620px] leading-relaxed">
            {t("hero.sub")}
          </p>
          <RotatingLine
            lines={t("hero.rotating")}
            className="mt-6 font-serif italic text-xl md:text-2xl text-navy"
          />
        </div>

        <div className="col-span-12 md:col-span-4 lg:col-span-4 md:col-start-9 lg:col-start-9 mt-10 anim-fade-up delay-500 md:text-right">
          <div className="eyebrow text-ink mb-2">01 / 04</div>
          <div className="text-sm text-ink max-w-[280px] md:ml-auto leading-relaxed">
            {t("hero.meta")}
          </div>
        </div>

        {/* CTAs */}
        <div className="col-span-12 mt-14 flex flex-wrap gap-3 anim-fade-up delay-500">
          <a
            data-testid="hero-cta-primary"
            href="#audit"
            className="btn-editorial btn-editorial-primary"
          >
            <Sparkles size={16} />
            {t("hero.cta_primary")}
            <ArrowRight size={16} />
          </a>
          <a
            data-testid="hero-cta-secondary"
            href="#work"
            className="btn-editorial btn-editorial-outline"
          >
            {t("hero.cta_secondary")}
          </a>
        </div>
      </div>

      {/* Decorative ticker bottom */}
      <div className="absolute bottom-6 left-0 right-0 max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between text-xs text-ink">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green animate-pulse" />
          <span>{new Date().getFullYear()} · Live & available</span>
        </div>
        <div className="eyebrow hidden md:block">Scroll ↓</div>
      </div>
    </section>
  );
}
